import Phaser from 'phaser';
import { BattleEffect, BattleEffectType, BattleEffectSequence, EffectTarget } from '../types/BattleEffect';
import { DistanceType } from '../types/Distance';
import { StanceType } from '../types/Stance';
import { DISTANCE_CHARACTER_POSITIONS } from './battleConfig';
import { EFFECT_CONFIG } from './battleConfig';
import { loadSettings, getEffectSpeedMultiplier } from '../utils/settingsManager';
import { playSe } from '../utils/audioManager';
import { AudioKey } from '../constants/audioKeys';
import { getMonsterEffect, TrailType } from '../constants/monsterEffects';

/**
 * エフェクト再生に必要なターゲットオブジェクト
 */
export type CharacterDisplayObject = Phaser.GameObjects.Image | Phaser.GameObjects.Sprite;

export interface EffectTargets {
  playerText: Phaser.GameObjects.Text;
  enemyText: Phaser.GameObjects.Text;
  playerImage?: CharacterDisplayObject;
  enemyImage?: CharacterDisplayObject;
  playerHpBarFill: Phaser.GameObjects.Rectangle;
  enemyHpBarFill: Phaser.GameObjects.Rectangle;
  playerMonsterId?: string;
  enemyMonsterId?: string;
  playerOrbColor?: number;
  enemyOrbColor?: number;
}

/**
 * バトルエフェクト再生クラス
 *
 * BattleEffectSequenceを受け取り、Phaser Tweensを使ってアニメーションを再生する。
 */
export class BattleEffectPlayer {
  private scene: Phaser.Scene;
  private targets: EffectTargets;
  private speedMultiplier: number;
  /** バトル終了フラグ: trueになると以降のplayIdle呼び出しを無効化 */
  private battleFinished = false;

  constructor(scene: Phaser.Scene, targets: EffectTargets) {
    this.scene = scene;
    this.targets = targets;
    const settings = loadSettings();
    this.speedMultiplier = getEffectSpeedMultiplier(settings.effectSpeed);
  }

  /**
   * エフェクトシーケンスを再生する
   * フェーズ内のエフェクトは同時実行、フェーズ間は順次実行
   * @param onPhaseComplete 各フェーズ終了後に呼ばれるコールバック（フェーズインデックスを渡す）
   */
  async playSequence(sequence: BattleEffectSequence, onPhaseComplete?: (phaseIndex: number) => void): Promise<void> {
    for (let i = 0; i < sequence.length; i++) {
      const phaseEffects = sequence[i];
      if (phaseEffects.length === 0) continue;
      await this.playPhase(phaseEffects);
      onPhaseComplete?.(i);
    }
  }

  /**
   * 1フェーズ内のエフェクトを同時再生する
   */
  private async playPhase(effects: BattleEffect[]): Promise<void> {
    const promises = effects.map(effect => this.playEffect(effect));
    await Promise.all(promises);
  }

  /**
   * 個別エフェクトを再生する
   */
  private playEffect(effect: BattleEffect): Promise<void> {
    switch (effect.type) {
      case BattleEffectType.DAMAGE_NUMBER:
        return this.playDamageNumber(effect);
      case BattleEffectType.WEAPON_ATTACK:
        return this.playWeaponAttack(effect);
      case BattleEffectType.SPECIAL_ATTACK:
        return this.playSpecialAttack(effect);
      case BattleEffectType.EVASION:
        return this.playEvasion(effect);
      case BattleEffectType.DISTANCE_MOVE:
        return this.playDistanceMove(effect);
      case BattleEffectType.STANCE_CHANGE:
        return this.playStanceChange(effect);
      case BattleEffectType.REFLECTOR_DEPLOY:
        return this.playReflectorDeploy(effect);
      case BattleEffectType.REFLECTOR_BLOCK:
        return this.playReflectorBlock(effect);
      case BattleEffectType.SPECIAL_REFLECT:
        return this.playSpecialReflect(effect);
      case BattleEffectType.SPECIAL_CHARGE_FIZZLE:
        return this.playSpecialChargeFizzle(effect);
      case BattleEffectType.SPECIAL_EVASION:
        return this.playSpecialEvasion(effect);
      case BattleEffectType.ADVANCE_RETREAT:
        return this.playAdvanceRetreat(effect);
      case BattleEffectType.WEAPON_MISS:
        return this.playWeaponMiss(effect);
      default:
        return Promise.resolve();
    }
  }

  /**
   * ターゲットに対応するテキストオブジェクトを取得
   */
  private getTargetText(target: EffectTarget): Phaser.GameObjects.Text {
    return target === 'player' ? this.targets.playerText : this.targets.enemyText;
  }

  /**
   * ターゲットに対応するエフェクト対象オブジェクトを取得
   * Imageがあればそちらを優先、なければTextにフォールバック
   */
  private getTargetObject(target: EffectTarget): CharacterDisplayObject | Phaser.GameObjects.Text {
    if (target === 'player') {
      return this.targets.playerImage ?? this.targets.playerText;
    }
    return this.targets.enemyImage ?? this.targets.enemyText;
  }

  /**
   * ターゲットに対応するスプライト（アニメーション可能な場合のみ）を取得
   */
  private getTargetSprite(target: EffectTarget): Phaser.GameObjects.Sprite | undefined {
    const img = target === 'player' ? this.targets.playerImage : this.targets.enemyImage;
    return img instanceof Phaser.GameObjects.Sprite ? img : undefined;
  }

  /**
   * 攻撃者（defenderの反対側）のオブジェクトを取得
   */
  private getAttackerObject(defenderTarget: EffectTarget): CharacterDisplayObject | Phaser.GameObjects.Text {
    return this.getTargetObject(defenderTarget === 'enemy' ? 'player' : 'enemy');
  }

  /**
   * 攻撃者スプライトを取得
   */
  private getAttackerSprite(defenderTarget: EffectTarget): Phaser.GameObjects.Sprite | undefined {
    return this.getTargetSprite(defenderTarget === 'enemy' ? 'player' : 'enemy');
  }

  /**
   * スプライトアニメーションを再生（スプライトでなければ何もしない）
   */
  playCharacterAnimation(target: EffectTarget, animKey: string, ignoreIfPlaying = false): void {
    const sprite = this.getTargetSprite(target);
    if (sprite && this.scene.anims.exists(animKey)) {
      sprite.play(animKey, ignoreIfPlaying);
    }
  }

  /** モンスターIDからアニメキーを生成 */
  private animKey(target: EffectTarget, action: string): string {
    const id = target === 'player' ? this.targets.playerMonsterId : this.targets.enemyMonsterId;
    return id ? `${id}_${action}` : '';
  }

  /** 指定ターゲットをidleに戻す（バトル終了後は呼ばない） */
  private playIdle(target: EffectTarget): void {
    if (this.battleFinished) return;
    this.playCharacterAnimation(target, this.animKey(target, 'idle'), true);
  }

  /**
   * 両キャラのidleアニメーションを再生（スプライトのみ）
   */
  playIdleAnimations(): void {
    this.playIdle('player');
    this.playIdle('enemy');
  }

  /**
   * 敗者のdownアニメを再生し、完了後にコールバックを呼ぶ
   * @param loserTarget 敗者側（'player' | 'enemy' | null = DRAW）
   * @param onComplete アニメ完了後のコールバック
   */
  playDownAnimation(loserTarget: EffectTarget | null, onComplete: () => void): void {
    this.battleFinished = true;
    if (!loserTarget) {
      onComplete();
      return;
    }
    const sprite = this.getTargetSprite(loserTarget);
    const downKey = this.animKey(loserTarget, 'down');

    if (!sprite || !this.scene.anims.exists(downKey)) {
      onComplete();
      return;
    }

    sprite.play(downKey);
    sprite.once('animationcomplete', onComplete);
  }

  /**
   * ダメージ数値表示: テキスト生成→上に浮かびながらフェードアウト
   */
  private playDamageNumber(effect: BattleEffect): Promise<void> {
    const targetObj = this.getTargetObject(effect.target);
    const damageText = this.scene.add.text(
      targetObj.x,
      targetObj.y - 30,
      `${effect.value}`,
      {
        fontSize: '24px',
        color: EFFECT_CONFIG.damageNumberColor,
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        fontStyle: 'bold',
      }
    );
    damageText.setOrigin(0.5);
    damageText.setScale(EFFECT_CONFIG.damageNumberPopScale);

    return new Promise<void>(resolve => {
      // スケール縮小（ポップ演出）
      this.scene.tweens.add({
        targets: damageText,
        scaleX: 1,
        scaleY: 1,
        duration: EFFECT_CONFIG.damageNumberPopDuration * this.speedMultiplier,
        ease: 'Back.out',
      });

      // 上移動 + フェードアウト（既存）
      this.scene.tweens.add({
        targets: damageText,
        y: damageText.y - 40,
        alpha: 0,
        duration: EFFECT_CONFIG.damageNumberDuration * this.speedMultiplier,
        ease: 'Power2',
        onComplete: () => {
          damageText.destroy();
          resolve();
        },
      });
    });
  }

  /**
   * 武器攻撃エフェクト: 攻撃者ダッシュ → スラッシュ線 + シェイク → 攻撃者リターン
   */
  private playWeaponAttack(effect: BattleEffect): Promise<void> {
    const targetObj = this.getTargetObject(effect.target);
    const attackerObj = this.getAttackerObject(effect.target);
    const originalAttackerX = attackerObj.x;
    const originalTargetX = targetObj.x;
    // 攻撃者が相手に向かって35%の距離をダッシュ
    const dashX = originalAttackerX + (originalTargetX - originalAttackerX) * 0.35;

    playSe(this.scene.sound, AudioKey.SE_WEAPON_ATTACK);

    const attackerTarget: EffectTarget = effect.target === 'enemy' ? 'player' : 'enemy';

    // 攻撃者: attackアニメ再生
    this.playCharacterAnimation(attackerTarget, this.animKey(attackerTarget, 'attack'));

    return new Promise<void>(resolve => {
      // Tween 1: 攻撃者ダッシュ
      this.scene.tweens.add({
        targets: attackerObj,
        x: dashX,
        duration: EFFECT_CONFIG.weaponDashDuration * this.speedMultiplier,
        ease: 'Power2.in',
        onComplete: () => {
          // ターゲット: hitアニメ再生
          this.playCharacterAnimation(effect.target, this.animKey(effect.target, 'hit'));

          // スラッシュGraphics描画
          const slash = this.scene.add.graphics();
          const sr = EFFECT_CONFIG.slashRange;
          slash.lineStyle(EFFECT_CONFIG.slashLineWidth, EFFECT_CONFIG.slashColor, 1);
          slash.beginPath();
          slash.moveTo(targetObj.x - sr * 0.5,  targetObj.y - sr * 0.7);
          slash.lineTo(targetObj.x + sr * 0.4,  targetObj.y + sr * 0.2);
          slash.moveTo(targetObj.x - sr * 0.3,  targetObj.y - sr * 0.9);
          slash.lineTo(targetObj.x + sr * 0.6,  targetObj.y);
          slash.strokePath();

          let done = 0;
          const checkDone = () => {
            done++;
            if (done >= 3) {
              targetObj.clearTint();
              resolve();
              // resolve後にidleに戻す（downアニメーションを上書きしないよう後回し）
              this.scene.time.delayedCall(0, () => {
                this.playIdle(attackerTarget);
                this.playIdle(effect.target);
              });
            }
          };

          // スラッシュフェードアウト
          this.scene.tweens.add({
            targets: slash,
            alpha: 0,
            duration: EFFECT_CONFIG.weaponAttackDuration * this.speedMultiplier / 2,
            ease: 'Power2',
            onComplete: () => {
              slash.destroy();
              checkDone();
            },
          });

          targetObj.setTint(EFFECT_CONFIG.weaponFlashColor);

          // ヒットパーティクル（火花）
          this.spawnHitParticles(targetObj.x, targetObj.y, 0xffffff, 0xffcc44);

          // カメラシェイク
          this.scene.cameras?.main?.shake(
            EFFECT_CONFIG.weaponAttackDuration * this.speedMultiplier * EFFECT_CONFIG.cameraShakeDurationRatio,
            EFFECT_CONFIG.cameraShakeIntensity
          );

          // Tween 2: 攻撃者リターン
          this.scene.tweens.add({
            targets: attackerObj,
            x: originalAttackerX,
            duration: EFFECT_CONFIG.weaponAttackDuration * this.speedMultiplier / 2,
            ease: 'Power2.out',
            onComplete: checkDone,
          });

          // Tween 3: ターゲットシェイク
          this.scene.tweens.add({
            targets: targetObj,
            x: originalTargetX + 8,
            duration: EFFECT_CONFIG.weaponAttackDuration * this.speedMultiplier / 4,
            yoyo: true,
            repeat: 0,
            ease: 'Sine.inOut',
            onComplete: () => {
              targetObj.x = originalTargetX;
              checkDone();
            },
          });
        },
      });
    });

  }

  /**
   * 武器攻撃空振りエフェクト: 攻撃者が短く踏み込んでスイング → 戻る（当たらない）
   * effect.target = 攻撃者側（WEAPON_ATTACKとは逆）
   */
  private playWeaponMiss(effect: BattleEffect): Promise<void> {
    const attackerObj = this.getTargetObject(effect.target);
    const opponentObj = this.getAttackerObject(effect.target);
    const originalAttackerX = attackerObj.x;
    // 相手方向に15%だけ踏み込む（WEAPON_ATTACKの35%より短い）
    const dashX = originalAttackerX + (opponentObj.x - originalAttackerX) * 0.15;

    // 攻撃者: attackアニメ再生
    this.playCharacterAnimation(effect.target, this.animKey(effect.target, 'attack'));

    return new Promise<void>(resolve => {
      // 踏み込み
      this.scene.tweens.add({
        targets: attackerObj,
        x: dashX,
        duration: EFFECT_CONFIG.weaponDashDuration * this.speedMultiplier,
        ease: 'Power2.in',
        onComplete: () => {
          // 戻る
          this.scene.tweens.add({
            targets: attackerObj,
            x: originalAttackerX,
            duration: EFFECT_CONFIG.weaponAttackDuration * this.speedMultiplier / 2,
            ease: 'Power2.out',
            onComplete: () => {
              resolve();
              this.scene.time.delayedCall(0, () => {
                this.playIdle(effect.target);
              });
            },
          });
        },
      });
    });
  }

  /**
   * 特殊攻撃エフェクト: 溜めグロー → 光球プロジェクタイル飛翔 → 衝撃パルス
   */
  private async playSpecialAttack(effect: BattleEffect): Promise<void> {
    const targetObj = this.getTargetObject(effect.target);
    const attackerObj = this.getAttackerObject(effect.target);
    const originalScaleX = targetObj.scaleX;
    const originalScaleY = targetObj.scaleY;
    const attackerTarget: EffectTarget = effect.target === 'enemy' ? 'player' : 'enemy';

    // 攻撃者: special アニメ再生
    this.playCharacterAnimation(attackerTarget, this.animKey(attackerTarget, 'special'));
    playSe(this.scene.sound, AudioKey.SE_SPECIAL_ATTACK);

    // Phase 0: 溜め（攻撃者がグロー）
    await new Promise<void>(resolve => {
      attackerObj.setTint(EFFECT_CONFIG.specialChargeColor);
      this.scene.tweens.add({
        targets: attackerObj,
        scaleX: attackerObj.scaleX * 1.15,
        scaleY: attackerObj.scaleY * 1.15,
        duration: EFFECT_CONFIG.specialChargeDuration * this.speedMultiplier,
        yoyo: true,
        onComplete: () => {
          attackerObj.clearTint();
          resolve();
        },
      });
    });

    const orbColor = (attackerTarget === 'player' ? this.targets.playerOrbColor : this.targets.enemyOrbColor)
      ?? EFFECT_CONFIG.orbColor;
    const orbGlowColor = (attackerTarget === 'player' ? this.targets.playerOrbColor : this.targets.enemyOrbColor)
      ?? EFFECT_CONFIG.orbGlowColor;

    // 魂格エフェクト設定を取得
    const attackerMonsterId = attackerTarget === 'player'
      ? this.targets.playerMonsterId : this.targets.enemyMonsterId;
    const monsterEffect = getMonsterEffect(attackerMonsterId);

    // 弱体化時はオーブを小さく・薄くする
    const weakenFactor = effect.isWeakened ? 0.6 : 1.0;
    const orbOuterR = EFFECT_CONFIG.orbOuterRadius * weakenFactor;
    const orbInnerR = EFFECT_CONFIG.orbInnerRadius * weakenFactor;
    const orbAlpha = effect.isWeakened ? 0.5 : 1.0;

    return new Promise<void>(resolve => {
      // 光球プロジェクタイル（魂格別画像）
      const orbDisplaySize = 128 * (effect.isWeakened ? 0.6 : 1.0); // 表示サイズ直接指定（px）
      const orbScale = orbDisplaySize / 128;
      const orb = this.scene.add.image(attackerObj.x, attackerObj.y, monsterEffect.orbImageKey);
      orb.setScale(orbScale * (attackerTarget === 'player' ? -1 : 1), orbScale);
      if (effect.isWeakened) {
        orb.setAlpha(orbAlpha);
        orb.setScale(orbScale * 0.6 * (attackerTarget === 'player' ? -1 : 1), orbScale * 0.6);
      }

      // トレイルパーティクル（魂格ごとの軌跡）
      const trailParticles: Phaser.GameObjects.Graphics[] = [];
      const trailInterval = this.scene.time.addEvent?.({
        delay: 60 * this.speedMultiplier,
        callback: () => {
          const trail = this.scene.add.graphics();
          trail.fillStyle(monsterEffect.particleColor, 0.65);
          switch (monsterEffect.trailType) {
            case 'rock': {
              const sz = 5 + Math.random() * 4;
              trail.fillRect(-sz / 2, -sz / 2, sz, sz);
              break;
            }
            case 'wind':
              trail.fillRect(-7, -1.5, 14, 3);
              break;
            case 'leaf':
              trail.fillTriangle(-5, 6, 5, 6, 0, -7);
              break;
            case 'claw':
              trail.lineStyle(2, monsterEffect.particleColor, 0.8);
              trail.moveTo(-3, -8);
              trail.lineTo(3, 8);
              trail.strokePath();
              break;
            case 'flame':
              trail.fillCircle(0, 0, 4 + Math.random() * 3);
              trail.setScale(0.6, 1.8);
              break;
            case 'water':
              trail.fillCircle(0, 0, 4 + Math.random() * 3);
              trail.setScale(0.8, 1.5);
              break;
            default: // wisp, neutral
              trail.fillCircle(0, 0, 5 + Math.random() * 4);
              break;
          }
          trail.x = orb.x + (Math.random() - 0.5) * 10;
          trail.y = orb.y + (Math.random() - 0.5) * 10;
          trailParticles.push(trail);
          this.scene.tweens.add({
            targets: trail,
            alpha: 0,
            duration: 200 * this.speedMultiplier,
            onComplete: () => { if (trail.scene) trail.destroy(); },
          });
        },
        repeat: 5,
      });

      // Tween 1: 光球がターゲットへ飛翔
      this.scene.tweens.add({
        targets: orb,
        x: targetObj.x,
        y: targetObj.y,
        duration: EFFECT_CONFIG.specialProjectileDuration * this.speedMultiplier,
        ease: 'Power2.in',
        onComplete: () => {
          orb.destroy();
          if (trailInterval?.destroy) trailInterval.destroy();
          trailParticles.forEach(p => { if (!p.scene) return; p.destroy(); });
          targetObj.setTint(EFFECT_CONFIG.specialPulseColor);

          // 着弾閃光パルス（大きな円が一瞬広がって消える）
          this.spawnImpactFlash(targetObj.x, targetObj.y, monsterEffect.particleColor);

          // ヒットパーティクル（魂格色・形状）
          this.spawnHitParticles(
            targetObj.x, targetObj.y,
            monsterEffect.particleColor, monsterEffect.particleSubColor,
            monsterEffect.trailType
          );

          // 光球命中と同時にダメージ数値を表示
          if (effect.value != null) {
            const damageText = this.scene.add.text(
              targetObj.x,
              targetObj.y - 30,
              `${effect.value}`,
              {
                fontSize: '24px',
                color: EFFECT_CONFIG.damageNumberColor,
                fontFamily: "'Noto Sans JP', Arial, sans-serif",
                fontStyle: 'bold',
              }
            );
            damageText.setOrigin(0.5);
            this.scene.tweens.add({
              targets: damageText,
              alpha: 0,
              y: damageText.y - 40,
              duration: EFFECT_CONFIG.damageNumberDuration * this.speedMultiplier,
              ease: 'Power2',
              onComplete: () => damageText.destroy(),
            });
          }

          // Tween 2: 衝撃パルス
          this.scene.tweens.add({
            targets: targetObj,
            scaleX: originalScaleX * 1.3,
            scaleY: originalScaleY * 1.3,
            duration: EFFECT_CONFIG.specialAttackDuration * this.speedMultiplier / 2,
            yoyo: true,
            ease: 'Sine.inOut',
            onComplete: () => {
              targetObj.clearTint();
              targetObj.scaleX = originalScaleX;
              targetObj.scaleY = originalScaleY;
              // 被弾側 hit → idle、攻撃側 idle に戻す
              this.playCharacterAnimation(effect.target, this.animKey(effect.target, 'hit'));
              const targetSprite = this.getTargetSprite(effect.target);
              targetSprite?.once('animationcomplete', () => this.playIdle(effect.target));
              this.playIdle(attackerTarget);
              resolve();
            },
          });
        },
      });
    });
  }

  /**
   * 回避エフェクト: 横ステップ + "MISS" テキスト
   */
  private playEvasion(effect: BattleEffect): Promise<void> {
    const targetObj = this.getTargetObject(effect.target);
    const originalX = targetObj.x;

    // dodge アニメ再生 → 完了後 idle に戻す
    this.playCharacterAnimation(effect.target, this.animKey(effect.target, 'dodge'));
    const sprite = this.getTargetSprite(effect.target);
    sprite?.once('animationcomplete', () => this.playIdle(effect.target));
    const missText = this.scene.add.text(
      targetObj.x,
      targetObj.y - 30,
      'MISS',
      {
        fontSize: '20px',
        color: EFFECT_CONFIG.evasionTextColor,
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        fontStyle: 'bold',
      }
    );
    missText.setOrigin(0.5);

    playSe(this.scene.sound, AudioKey.SE_DODGE);

    return new Promise<void>(resolve => {
      this.scene.tweens.add({
        targets: targetObj,
        x: originalX + (effect.target === 'player' ? -30 : 30),
        duration: EFFECT_CONFIG.evasionDuration * this.speedMultiplier / 2,
        yoyo: true,
        ease: 'Power2',
        onComplete: () => {
          targetObj.x = originalX;
        },
      });

      this.scene.tweens.add({
        targets: missText,
        alpha: 0,
        y: missText.y - 20,
        duration: EFFECT_CONFIG.evasionDuration * this.speedMultiplier,
        ease: 'Power2',
        onComplete: () => {
          missText.destroy();
          resolve();
        },
      });
    });
  }

  /**
   * 距離移動アニメーション: キャラクター位置のTween移動
   * Image+Text両方を同時に移動させる
   */
  private playDistanceMove(effect: BattleEffect): Promise<void> {
    if (!effect.distanceTo) return Promise.resolve();

    const newPositions = DISTANCE_CHARACTER_POSITIONS[effect.distanceTo];
    const playerText = this.targets.playerText;
    const enemyText = this.targets.enemyText;
    const playerImage = this.targets.playerImage;
    const enemyImage = this.targets.enemyImage;

    // 移動方向を判定してアニメ再生（playerは右向きが前進、enemyは左向きが前進）
    const playerMoving = playerImage && playerImage.x !== newPositions.playerX;
    const enemyMoving = enemyImage && enemyImage.x !== newPositions.enemyX;
    if (playerMoving) {
      const isForward = newPositions.playerX > playerImage!.x;
      this.playCharacterAnimation('player', this.animKey('player', isForward ? 'move_f' : 'move_b'));
    }
    if (enemyMoving) {
      const isForward = newPositions.enemyX < enemyImage!.x; // 敵は左向きなので逆
      this.playCharacterAnimation('enemy', this.animKey('enemy', isForward ? 'move_f' : 'move_b'));
    }

    playSe(this.scene.sound, AudioKey.SE_MOVE);

    return new Promise<void>(resolve => {
      let completed = 0;
      const totalExpected = 2 + (playerImage ? 1 : 0) + (enemyImage ? 1 : 0);
      const onOneComplete = () => {
        completed++;
        if (completed >= totalExpected) {
          // 移動完了後 idle に戻す
          if (playerMoving) this.playIdle('player');
          if (enemyMoving) this.playIdle('enemy');
          resolve();
        }
      };

      // Text移動
      this.scene.tweens.add({
        targets: playerText,
        x: newPositions.playerX,
        duration: EFFECT_CONFIG.distanceMoveDuration * this.speedMultiplier,
        ease: 'Power2',
        onComplete: onOneComplete,
      });

      this.scene.tweens.add({
        targets: enemyText,
        x: newPositions.enemyX,
        duration: EFFECT_CONFIG.distanceMoveDuration * this.speedMultiplier,
        ease: 'Power2',
        onComplete: onOneComplete,
      });

      // Image移動（存在する場合、完了カウントに含める）
      if (playerImage) {
        this.scene.tweens.add({
          targets: playerImage,
          x: newPositions.playerX,
          duration: EFFECT_CONFIG.distanceMoveDuration * this.speedMultiplier,
          ease: 'Power2',
          onComplete: onOneComplete,
        });
      }

      if (enemyImage) {
        this.scene.tweens.add({
          targets: enemyImage,
          x: newPositions.enemyX,
          duration: EFFECT_CONFIG.distanceMoveDuration * this.speedMultiplier,
          ease: 'Power2',
          onComplete: onOneComplete,
        });
      }
    });
  }

  /**
   * リフレクター構えエフェクト: 淡い青盾テキスト（攻撃が来なかった場合）
   */
  private playReflectorDeploy(effect: BattleEffect): Promise<void> {
    const targetObj = this.getTargetObject(effect.target);
    const deployText = this.scene.add.text(
      targetObj.x,
      targetObj.y - 40,
      '🛡️',
      {
        fontSize: '22px',
        color: EFFECT_CONFIG.reflectorDeployTextColor,
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
      }
    );
    deployText.setOrigin(0.5);

    return new Promise<void>(resolve => {
      targetObj.setTint(EFFECT_CONFIG.reflectorShieldColor);
      this.spawnBarrierParticles(
        targetObj.x, targetObj.y,
        EFFECT_CONFIG.reflectorDeployParticleCount,
        EFFECT_CONFIG.reflectorDeployParticleColor,
        EFFECT_CONFIG.reflectorDeployParticleSpread,
      );

      this.scene.tweens.add({
        targets: deployText,
        alpha: 0,
        y: deployText.y - 15,
        duration: EFFECT_CONFIG.reflectorDeployDuration * this.speedMultiplier,
        ease: 'Power2',
        onComplete: () => {
          targetObj.clearTint();
          deployText.destroy();
          resolve();
        },
      });
    });
  }

  /**
   * スタンス変更エフェクト: カラーティント + スタンス名テキスト
   */
  private playStanceChange(effect: BattleEffect): Promise<void> {
    const targetObj = this.getTargetObject(effect.target);

    let tintColor: number;
    let stanceText: string;
    let textColor: string;

    switch (effect.stanceTo) {
      case StanceType.OFFENSIVE:
        tintColor = EFFECT_CONFIG.stanceOffensiveColor;
        stanceText = '攻勢！';
        textColor = '#ff8800';
        break;
      case StanceType.DEFENSIVE:
        tintColor = EFFECT_CONFIG.stanceDefensiveColor;
        stanceText = '守勢！';
        textColor = '#4488ff';
        break;
      default:
        tintColor = EFFECT_CONFIG.stanceNormalColor;
        stanceText = '通常';
        textColor = '#aaaaaa';
    }

    const stanceLabel = this.scene.add.text(
      targetObj.x,
      targetObj.y - 30,
      stanceText,
      {
        fontSize: '20px',
        color: textColor,
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        fontStyle: 'bold',
      }
    );
    stanceLabel.setOrigin(0.5);
    stanceLabel.setScale(EFFECT_CONFIG.stanceChangePopScale);
    targetObj.setTint(tintColor);

    return new Promise<void>(resolve => {
      // スケール縮小（ポップ演出）
      this.scene.tweens.add({
        targets: stanceLabel,
        scaleX: 1,
        scaleY: 1,
        duration: EFFECT_CONFIG.stanceChangePopDuration * this.speedMultiplier,
        ease: 'Back.out',
      });

      // 上移動 + フェードアウト（既存）
      this.scene.tweens.add({
        targets: stanceLabel,
        alpha: 0,
        y: stanceLabel.y - 20,
        duration: EFFECT_CONFIG.stanceChangeDuration * this.speedMultiplier,
        ease: 'Power2',
        onComplete: () => {
          targetObj.clearTint();
          stanceLabel.destroy();
          resolve();
        },
      });
    });
  }

  /**
   * SPECIAL×REFLECTOR 反射アニメーション（4ステップ連続）
   *
   * ① 攻撃者から光球が防御者へ飛翔
   * ② 防御者が盾を構える（青ティント + 🛡）
   * ③ 光球が反転して攻撃者へ跳ね返る
   * ④ 攻撃者が被弾（フラッシュ + ダメージ数値）
   *
   * effect.target = 防御者（リフレクター保持者）
   * effect.reflectedDamage = 跳ね返り後のダメージ
   */
  private async playSpecialReflect(effect: BattleEffect): Promise<void> {
    const defenderObj = this.getTargetObject(effect.target);
    const attackerTarget: EffectTarget = effect.target === 'player' ? 'enemy' : 'player';
    const attackerObj = this.getTargetObject(attackerTarget);
    const cfg = EFFECT_CONFIG;
    const attackerMonsterId = attackerTarget === 'player'
      ? this.targets.playerMonsterId : this.targets.enemyMonsterId;
    const attackerMonsterEffect = getMonsterEffect(attackerMonsterId);

    // Phase 0: 溜め（攻撃者がグロー＋specialアニメ）
    this.playCharacterAnimation(attackerTarget, this.animKey(attackerTarget, 'special'));
    playSe(this.scene.sound, AudioKey.SE_SPECIAL_ATTACK);
    await new Promise<void>(resolve => {
      attackerObj.setTint(cfg.specialChargeColor);
      this.scene.tweens.add({
        targets: attackerObj,
        scaleX: attackerObj.scaleX * 1.15,
        scaleY: attackerObj.scaleY * 1.15,
        duration: cfg.specialChargeDuration * this.speedMultiplier,
        yoyo: true,
        onComplete: () => {
          attackerObj.clearTint();
          resolve();
        },
      });
    });

    // ① 光球: 攻撃者 → 防御者
    await new Promise<void>(resolve => {
      const orbScale = 128 / 128;
      const orb = this.scene.add.image(attackerObj.x, attackerObj.y, attackerMonsterEffect.orbImageKey);
      orb.setScale(orbScale * (attackerTarget === 'player' ? -1 : 1), orbScale);

      this.scene.tweens.add({
        targets: orb,
        x: defenderObj.x,
        y: defenderObj.y,
        duration: cfg.specialReflectProjectileDuration * this.speedMultiplier,
        ease: 'Power2.in',
        onComplete: () => { orb.destroy(); resolve(); },
      });
    });

    // ② 防御者が盾を構える
    playSe(this.scene.sound, AudioKey.SE_REFLECT);
    await new Promise<void>(resolve => {
      const shieldText = this.scene.add.text(
        defenderObj.x,
        defenderObj.y - 45,
        '🛡️',
        { fontSize: '30px', fontFamily: "'Noto Sans JP', Arial, sans-serif" },
      ).setOrigin(0.5);

      defenderObj.setTint(cfg.reflectorShieldColor);

      this.scene.tweens.add({
        targets: shieldText,
        y: shieldText.y - 12,
        duration: cfg.specialReflectShieldDuration * this.speedMultiplier,
        ease: 'Power2.out',
        onComplete: () => {
          defenderObj.clearTint();
          shieldText.destroy();
          resolve();
        },
      });
    });

    // ③ 反射光球: 防御者 → 攻撃者（反転して跳ね返り感を演出）
    await new Promise<void>(resolve => {
      const reflectOrbScale = 128 / 128;
      // 反射方向は攻撃者へ向かうので flipX を逆にする
      const reflectOrb = this.scene.add.image(defenderObj.x, defenderObj.y, attackerMonsterEffect.orbImageKey);
      reflectOrb.setScale(reflectOrbScale * (attackerTarget === 'player' ? 1 : -1), reflectOrbScale);
      reflectOrb.setTint(cfg.specialReflectOrbColor);

      const reflectLabel = this.scene.add.text(
        defenderObj.x,
        defenderObj.y - 30,
        'REFLECT!',
        {
          fontSize: '18px',
          color: '#ff8844',
          fontFamily: "'Noto Sans JP', Arial, sans-serif",
          fontStyle: 'bold',
        },
      ).setOrigin(0.5);

      this.scene.tweens.add({
        targets: reflectOrb,
        x: attackerObj.x,
        y: attackerObj.y,
        duration: cfg.specialReflectReturnDuration * this.speedMultiplier,
        ease: 'Power2.in',
        onComplete: () => { reflectOrb.destroy(); resolve(); },
      });

      this.scene.tweens.add({
        targets: reflectLabel,
        alpha: 0,
        duration: cfg.specialReflectReturnDuration * this.speedMultiplier,
        ease: 'Power2',
        onComplete: () => { reflectLabel.destroy(); },
      });
    });

    // ④ 攻撃者が被弾（フラッシュ + ダメージ数値）
    this.spawnBarrierParticles(
      attackerObj.x, attackerObj.y,
      cfg.reflectHitParticleCount,
      cfg.reflectHitParticleColor,
      cfg.reflectHitParticleSpread,
    );
    const originalScaleX = attackerObj.scaleX;
    const originalScaleY = attackerObj.scaleY;
    attackerObj.setTint(cfg.specialPulseColor);

    const damage = effect.reflectedDamage ?? 0;
    if (damage > 0) {
      const damageText = this.scene.add.text(
        attackerObj.x,
        attackerObj.y - 30,
        `${damage}`,
        {
          fontSize: '24px',
          color: cfg.damageNumberColor,
          fontFamily: "'Noto Sans JP', Arial, sans-serif",
          fontStyle: 'bold',
        },
      ).setOrigin(0.5);

      this.scene.tweens.add({
        targets: damageText,
        y: damageText.y - 40,
        alpha: 0,
        duration: cfg.damageNumberDuration * this.speedMultiplier,
        ease: 'Power2',
        onComplete: () => { damageText.destroy(); },
      });
    }

    await new Promise<void>(resolve => {
      this.scene.tweens.add({
        targets: attackerObj,
        scaleX: originalScaleX * 1.25,
        scaleY: originalScaleY * 1.25,
        duration: cfg.specialReflectHitDuration * this.speedMultiplier / 2,
        yoyo: true,
        ease: 'Sine.inOut',
        onComplete: () => {
          attackerObj.clearTint();
          attackerObj.scaleX = originalScaleX;
          attackerObj.scaleY = originalScaleY;
          // 攻撃者の hit アニメ → 完了後 idle に戻す
          this.playCharacterAnimation(attackerTarget, this.animKey(attackerTarget, 'hit'));
          const attackerSprite = this.getTargetSprite(attackerTarget);
          attackerSprite?.once('animationcomplete', () => this.playIdle(attackerTarget));
          resolve();
        },
      });
    });
  }

  /**
   * 特殊攻撃が武器攻撃で潰された演出: 溜めグロー → エネルギー散逸（fizzle）
   * 武器攻撃エフェクトと並列再生されるため、溜め途中に武器が当たる演出になる
   */
  private async playSpecialChargeFizzle(effect: BattleEffect): Promise<void> {
    const attackerObj = this.getTargetObject(effect.target);
    const cfg = EFFECT_CONFIG;
    const originalScaleX = attackerObj.scaleX;
    const originalScaleY = attackerObj.scaleY;

    // Phase 1: 溜め（specialアニメ + グロー）
    this.playCharacterAnimation(effect.target, this.animKey(effect.target, 'special'));
    await new Promise<void>(resolve => {
      attackerObj.setTint(cfg.specialChargeColor);
      this.scene.tweens.add({
        targets: attackerObj,
        scaleX: originalScaleX * 1.15,
        scaleY: originalScaleY * 1.15,
        duration: cfg.specialChargeDuration * this.speedMultiplier,
        yoyo: true,
        onComplete: () => resolve(),
      });
    });

    // Phase 2: 崩れ（ティントをクリアしてスケール元に戻す）
    await new Promise<void>(resolve => {
      attackerObj.clearTint();
      this.scene.tweens.add({
        targets: attackerObj,
        scaleX: originalScaleX,
        scaleY: originalScaleY,
        duration: cfg.specialChargeFizzleDuration * this.speedMultiplier,
        ease: 'Power2',
        onComplete: () => resolve(),
      });
    });
  }

  /**
   * 特殊攻撃が回避された演出: 溜めグロー → 光球飛翔 → 着弾瞬間に防御者ステップ回避 + MISS テキスト
   */
  private async playSpecialEvasion(effect: BattleEffect): Promise<void> {
    const defenderObj = this.getTargetObject(effect.target);
    const attackerObj = this.getAttackerObject(effect.target);
    const originalDefenderX = defenderObj.x;
    const cfg = EFFECT_CONFIG;

    const attackerTarget: EffectTarget = effect.target === 'enemy' ? 'player' : 'enemy';

    // Phase 0: 溜め（攻撃者グロー）
    this.playCharacterAnimation(attackerTarget, this.animKey(attackerTarget, 'special'));
    playSe(this.scene.sound, AudioKey.SE_SPECIAL_ATTACK);
    await new Promise<void>(resolve => {
      attackerObj.setTint(cfg.specialChargeColor);
      this.scene.tweens.add({
        targets: attackerObj,
        scaleX: attackerObj.scaleX * 1.15,
        scaleY: attackerObj.scaleY * 1.15,
        duration: cfg.specialChargeDuration * this.speedMultiplier,
        yoyo: true,
        onComplete: () => {
          attackerObj.clearTint();
          resolve();
        },
      });
    });

    // 攻撃者の魂格設定を取得
    const attackerMonsterId = attackerTarget === 'player'
      ? this.targets.playerMonsterId : this.targets.enemyMonsterId;
    const monsterEffect = getMonsterEffect(attackerMonsterId);
    const orbColor = (attackerTarget === 'player' ? this.targets.playerOrbColor : this.targets.enemyOrbColor)
      ?? cfg.orbColor;
    const orbGlowColor = (attackerTarget === 'player' ? this.targets.playerOrbColor : this.targets.enemyOrbColor)
      ?? cfg.orbGlowColor;

    // Phase 1: 光球飛翔 → 着弾瞬間に回避
    await new Promise<void>(resolve => {
      const orbScale = 128 / 128;
      const orb = this.scene.add.image(attackerObj.x, attackerObj.y, monsterEffect.orbImageKey);
      orb.setScale(orbScale * (attackerTarget === 'player' ? -1 : 1), orbScale);

      // トレイルパーティクル（魂格ごとの軌跡）
      const trailParticles: Phaser.GameObjects.Graphics[] = [];
      const trailInterval = this.scene.time.addEvent?.({
        delay: 60 * this.speedMultiplier,
        callback: () => {
          const trail = this.scene.add.graphics();
          trail.fillStyle(monsterEffect.particleColor, 0.65);
          switch (monsterEffect.trailType) {
            case 'rock': { const sz = 5 + Math.random() * 4; trail.fillRect(-sz/2, -sz/2, sz, sz); break; }
            case 'wind': trail.fillRect(-7, -1.5, 14, 3); break;
            case 'leaf': trail.fillTriangle(-5, 6, 5, 6, 0, -7); break;
            case 'claw':
              trail.lineStyle(2, monsterEffect.particleColor, 0.8);
              trail.moveTo(-3, -8); trail.lineTo(3, 8); trail.strokePath();
              break;
            case 'flame': trail.fillCircle(0, 0, 4 + Math.random() * 3); trail.setScale(0.6, 1.8); break;
            case 'water': trail.fillCircle(0, 0, 4 + Math.random() * 3); trail.setScale(0.8, 1.5); break;
            default: trail.fillCircle(0, 0, 5 + Math.random() * 4); break;
          }
          trail.x = orb.x + (Math.random() - 0.5) * 10;
          trail.y = orb.y + (Math.random() - 0.5) * 10;
          trailParticles.push(trail);
          this.scene.tweens.add({
            targets: trail,
            alpha: 0,
            duration: 200 * this.speedMultiplier,
            onComplete: () => { if (trail.scene) trail.destroy(); },
          });
        },
        repeat: 5,
      });

      this.scene.tweens.add({
        targets: orb,
        x: defenderObj.x,
        y: defenderObj.y,
        duration: cfg.specialProjectileDuration * this.speedMultiplier,
        ease: 'Power2.in',
        onComplete: () => {
          orb.destroy();
          if (trailInterval?.destroy) trailInterval.destroy();
          trailParticles.forEach(p => { if (!p.scene) return; p.destroy(); });

          // 着弾と同時: MISS テキスト + 防御者ステップ回避
          const missText = this.scene.add.text(
            defenderObj.x,
            defenderObj.y - 30,
            'MISS',
            {
              fontSize: '20px',
              color: cfg.evasionTextColor,
              fontFamily: "'Noto Sans JP', Arial, sans-serif",
              fontStyle: 'bold',
            }
          ).setOrigin(0.5);

          this.scene.tweens.add({
            targets: defenderObj,
            x: originalDefenderX + (effect.target === 'player' ? -30 : 30),
            duration: cfg.evasionDuration * this.speedMultiplier / 2,
            yoyo: true,
            ease: 'Power2',
            onComplete: () => {
              defenderObj.x = originalDefenderX;
            },
          });

          this.scene.tweens.add({
            targets: missText,
            alpha: 0,
            y: missText.y - 20,
            duration: cfg.evasionDuration * this.speedMultiplier,
            ease: 'Power2',
            onComplete: () => {
              missText.destroy();
              resolve();
            },
          });
        },
      });
    });
  }

  /**
   * リフレクター残回数切れブロックエフェクト
   * 特殊攻撃をブロックしたが反射できなかった場合。
   * 溜め → 光球飛翔 → 着弾でGUARDテキスト の順番で再生する。
   */
  private async playReflectorBlock(effect: BattleEffect): Promise<void> {
    const defenderObj = this.getTargetObject(effect.target);
    const attackerObj = this.getAttackerObject(effect.target);
    const cfg = EFFECT_CONFIG;

    // Phase 0: 攻撃者が溜める（グロー + スケール + specialアニメ）
    const attackerTarget: EffectTarget = effect.target === 'player' ? 'enemy' : 'player';
    this.playCharacterAnimation(attackerTarget, this.animKey(attackerTarget, 'special'));
    playSe(this.scene.sound, AudioKey.SE_SPECIAL_ATTACK);
    await new Promise<void>(resolve => {
      attackerObj.setTint(cfg.specialChargeColor);
      this.scene.tweens.add({
        targets: attackerObj,
        scaleX: attackerObj.scaleX * 1.15,
        scaleY: attackerObj.scaleY * 1.15,
        duration: cfg.specialChargeDuration * this.speedMultiplier,
        yoyo: true,
        onComplete: () => {
          attackerObj.clearTint();
          resolve();
        },
      });
    });

    // Phase 1: オーブが攻撃者 → 防御者へ飛翔
    const attackerMonsterId = attackerTarget === 'player'
      ? this.targets.playerMonsterId
      : this.targets.enemyMonsterId;
    const attackerMonsterEffect = getMonsterEffect(attackerMonsterId);
    await new Promise<void>(resolve => {
      const orb = this.scene.add.image(attackerObj.x, attackerObj.y, attackerMonsterEffect.orbImageKey);
      orb.setScale(attackerTarget === 'player' ? -1 : 1, 1);

      this.scene.tweens.add({
        targets: orb,
        x: defenderObj.x,
        y: defenderObj.y,
        duration: cfg.specialReflectProjectileDuration * this.speedMultiplier,
        ease: 'Power2.in',
        onComplete: () => {
          orb.destroy();
          resolve();
        },
      });
    });

    // Phase 2: 着弾 → 防御者が GUARD（ティント + テキスト）
    playSe(this.scene.sound, AudioKey.SE_REFLECT_BLOCK);
    this.spawnBarrierParticles(
      defenderObj.x, defenderObj.y,
      cfg.reflectorBlockParticleCount,
      cfg.reflectorBlockParticleColor,
      cfg.reflectorBlockParticleSpread,
    );
    const blockText = this.scene.add.text(
      defenderObj.x,
      defenderObj.y - 40,
      'GUARD',
      {
        fontSize: '20px',
        color: cfg.reflectorBlockTextColor,
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        fontStyle: 'bold',
      }
    );
    blockText.setOrigin(0.5);

    await new Promise<void>(resolve => {
      defenderObj.setTint(cfg.reflectorBlockTintColor);

      this.scene.tweens.add({
        targets: blockText,
        alpha: 0,
        y: blockText.y - 15,
        duration: cfg.reflectorBlockDuration * this.speedMultiplier,
        ease: 'Power2',
        onComplete: () => {
          defenderObj.clearTint();
          blockText.destroy();
          resolve();
        },
      });
    });
  }

  /**
   * 前進+後退相殺エフェクト: 双方がわずかに動いて戻るヌッジアニメーション
   * advancerTarget が前進側。お互いに動いたことを視覚的に見せる。
   */
  private playAdvanceRetreat(effect: BattleEffect): Promise<void> {
    const advancerTarget = effect.advancerTarget ?? 'player';
    const retreaterTarget: EffectTarget = advancerTarget === 'player' ? 'enemy' : 'player';
    const advancerObj = this.getTargetObject(advancerTarget);
    const retreaterObj = this.getTargetObject(retreaterTarget);
    const originalAdvancerX = advancerObj.x;
    const originalRetreaterX = retreaterObj.x;
    const cfg = EFFECT_CONFIG;

    // 前進側は相手方向へ、後退側は相手から離れる方向へ移動してから戻る
    const nudge = 30;
    const advancerNudgeX = advancerTarget === 'player'
      ? originalAdvancerX + nudge   // player が前進 → 右（enemyに近づく）
      : originalAdvancerX - nudge;  // enemy が前進 → 左（playerに近づく）
    const retreaterNudgeX = retreaterTarget === 'player'
      ? originalRetreaterX - nudge  // player が後退 → 左（enemyから離れる）
      : originalRetreaterX + nudge; // enemy が後退 → 右（playerから離れる）

    const duration = cfg.distanceMoveDuration * this.speedMultiplier * 0.4;
    const zoomDuration = cfg.advanceRetreatZoomDuration * this.speedMultiplier;
    const cam = this.scene.cameras?.main;
    // コールバック内で直接zoomToを呼ぶと、直後のeffectComplete()がisRunning=falseにして
    // 新しいズームをキャンセルしてしまう。delayedCall(0)で次フレームに遅延させることで回避する。
    cam?.zoomTo(cfg.advanceRetreatZoomIn, zoomDuration, 'Linear', false,
      (_c: unknown, progress: number) => {
        if (progress >= 1) {
          this.scene.time.delayedCall(0, () => cam.zoomTo(1, zoomDuration, 'Linear'));
        }
      }
    );

    return new Promise<void>(resolve => {
      let completed = 0;
      const onDone = () => {
        completed++;
        if (completed >= 2) resolve();
      };

      // 前進側ヌッジ（行って戻る）
      this.scene.tweens.add({
        targets: advancerObj,
        x: advancerNudgeX,
        duration,
        ease: 'Power2.in',
        yoyo: true,
        onComplete: () => {
          advancerObj.x = originalAdvancerX;
          onDone();
        },
      });

      // 後退側ヌッジ（行って戻る）
      this.scene.tweens.add({
        targets: retreaterObj,
        x: retreaterNudgeX,
        duration,
        ease: 'Power2.in',
        yoyo: true,
        onComplete: () => {
          retreaterObj.x = originalRetreaterX;
          onDone();
        },
      });
    });
  }

  /**
   * 着弾時の閃光パルス（円が広がって消える爆発感）
   */
  private spawnImpactFlash(x: number, y: number, color: number): void {
    // 外側リング（広がるアウトライン）
    const ring = this.scene.add.graphics();
    ring.lineStyle(4, color, 0.9);
    ring.strokeCircle(0, 0, 10);
    ring.x = x;
    ring.y = y;
    this.scene.tweens.add({
      targets: ring,
      scaleX: 5,
      scaleY: 5,
      alpha: 0,
      duration: 250 * this.speedMultiplier,
      ease: 'Power2.out',
      onComplete: () => ring.destroy(),
    });

    // 内側フラッシュ（白い円が一瞬光って消える）
    const flash = this.scene.add.graphics();
    flash.fillStyle(0xffffff, 0.8);
    flash.fillCircle(0, 0, 20);
    flash.x = x;
    flash.y = y;
    this.scene.tweens.add({
      targets: flash,
      scaleX: 3,
      scaleY: 3,
      alpha: 0,
      duration: 180 * this.speedMultiplier,
      ease: 'Power3.out',
      onComplete: () => flash.destroy(),
    });
  }

  /**
   * ヒット地点にパーティクル（火花）を散らす
   * Phaser Graphicsで小さな円を複数生成し、放射状にフェードアウトさせる
   */
  private spawnHitParticles(
    x: number, y: number, color: number, subColor: number,
    trailType: TrailType = 'neutral'
  ): void {
    if (trailType === 'claw') {
      // 3本の太い斜め爪痕ライン（画面に残る）
      for (let i = 0; i < 3; i++) {
        const particle = this.scene.add.graphics();
        particle.lineStyle(3, i % 2 === 0 ? color : subColor, 1.0);
        const offsetX = (i - 1) * 14;
        particle.moveTo(offsetX - 8, -20);
        particle.lineTo(offsetX + 8, 20);
        particle.strokePath();
        particle.x = x;
        particle.y = y;
        this.scene.tweens.add({
          targets: particle,
          alpha: 0,
          duration: 350 * this.speedMultiplier,
          onComplete: () => particle.destroy(),
        });
      }
      return;
    }

    const count = 8;
    for (let i = 0; i < count; i++) {
      const particle = this.scene.add.graphics();
      const useSubColor = i % 2 === 0;
      particle.fillStyle(useSubColor ? subColor : color, 0.95);

      const angle = (Math.PI * 2 / count) * i + Math.random() * 0.4;
      let dx = Math.cos(angle);
      let dy = Math.sin(angle);

      switch (trailType) {
        case 'rock': {
          // 大きめの四角、下方向に落下
          const sz = 6 + Math.random() * 5;
          particle.fillRect(-sz / 2, -sz / 2, sz, sz);
          dy = Math.abs(dy) + 0.6; // 重力：必ず下方向
          break;
        }
        case 'wind':
          // 細長い横棒
          particle.fillRect(-10, -1.5, 18 + Math.random() * 8, 3);
          dx *= 1.8; // 水平方向強め
          dy *= 0.15;
          break;
        case 'leaf':
          // 大きめの三角形
          particle.fillTriangle(-6, 7, 6, 7, 0, -8);
          break;
        case 'flame':
          // 縦長楕円: scaleYを2倍にして縦長に
          particle.fillCircle(0, 0, 4 + Math.random() * 3);
          particle.setScale(0.6, 1.8);
          dy -= 0.8; // 強い上方向バイアス
          break;
        case 'wisp':
          // 大きめの丸、ふわっと広がる
          particle.fillCircle(0, 0, 5 + Math.random() * 4);
          dx *= 0.6;
          dy *= 0.6;
          break;
        case 'water':
          // 雫型: 下向きに伸びた楕円
          particle.fillCircle(0, 0, 4 + Math.random() * 3);
          particle.setScale(0.8, 1.6);
          dy = Math.abs(dy) + 0.4; // 必ず下
          break;
        default: // neutral
          particle.fillCircle(0, 0, 4 + Math.random() * 3);
          break;
      }

      particle.x = x;
      particle.y = y;
      const dist = 28 + Math.random() * 24;

      this.scene.tweens.add({
        targets: particle,
        x: x + dx * dist,
        y: y + dy * dist,
        alpha: 0,
        duration: 300 * this.speedMultiplier,
        ease: 'Power2.out',
        onComplete: () => particle.destroy(),
      });
    }
  }

  /**
   * パーティクルを放射状に散布する（バリア展開・反射ヒット・ブロック用）
   */
  private spawnBarrierParticles(
    x: number,
    y: number,
    count: number,
    color: number,
    spread: number,
  ): void {
    const cfg = EFFECT_CONFIG;
    for (let i = 0; i < count; i++) {
      const angle = (i / count) * Math.PI * 2;
      const particle = this.scene.add.graphics();
      particle.fillStyle(color, 0.9);
      particle.fillCircle(0, 0, 4);
      particle.x = x;
      particle.y = y;

      this.scene.tweens.add({
        targets: particle,
        x: x + Math.cos(angle) * spread,
        y: y + Math.sin(angle) * spread,
        alpha: 0,
        duration: cfg.particleFadeDuration * this.speedMultiplier,
        ease: 'Power2.out',
        onComplete: () => { particle.destroy(); },
      });
    }
  }
}
